// Small view-model helpers derived from API data (pure presentation math).
import { useMemo } from 'react';
import type { PositionRow, WeekEvent } from '@/types';
import { useCalendar, useMarket, usePortfolioData } from './queries';
import { useTime } from './useTime';
import { nowMs } from '@/lib/time';

export function usePortfolio() {
  const { holdings, updatedAt, totals } = usePortfolioData();
  const { universe } = useMarket();
  return useMemo(() => {
    // ORCA API: the server values every position (Decimal); the browser only shows it
    if (totals) {
      const rows = holdings.map((h) => {
        const w = universe.find((c) => c.symbol === h.symbol);
        const v = h.valuation;
        return {
          ...h, name: w?.name ?? h.symbol, price: v?.price ?? w?.price ?? 0, decimals: w?.decimals ?? 2,
          change24h: v?.change24h ?? w?.change24h ?? 0, value: v?.value ?? 0, pnl: v?.pnl ?? 0, pnlPct: v?.pnlPct ?? 0,
          day: v?.day ?? 0, share: v?.share ?? 0, unpriced: v?.value == null,
        } as PositionRow;
      });
      return {
        rows, total: totals.value, cost: totals.cost, day: totals.day, dayPct: totals.dayPct ?? 0,
        pnl: totals.pnl ?? 0, pnlPct: totals.pnlPct ?? 0, pnlKnown: totals.pnl !== null,
        estimated: totals.estimated, incomplete: totals.incomplete, unpriced: totals.unpriced, updatedAt,
      };
    }
    // DEV mock: the same presentation math the mock server stands in for
    const rows = holdings.map((h) => {
      const w = universe.find((c) => c.symbol === h.symbol)!;
      const value = h.qty * w.price;
      // P/L only over what is known: never invent a price for a purchase without one
      const pnl = h.incomplete ? 0 : value - h.cost;
      return {
        ...h, name: w.name, price: w.price, decimals: w.decimals, change24h: w.change24h,
        value, pnl, pnlPct: !h.incomplete && h.cost ? (pnl / h.cost) * 100 : 0,
        day: value - value / (1 + w.change24h / 100), share: 0,
      } as PositionRow;
    });
    const total = rows.reduce((a, r) => a + r.value, 0);
    // totals of P/L cover complete positions only; «≈» when any of them has an estimated price
    const known = rows.filter((r) => !r.incomplete);
    const cost = known.reduce((a, r) => a + r.cost, 0);
    const knownValue = known.reduce((a, r) => a + r.value, 0);
    const day = rows.reduce((a, r) => a + r.day, 0);
    rows.forEach((r) => { r.share = total ? (r.value / total) * 100 : 0; });
    return {
      rows, total, cost, day, dayPct: total ? (day / (total - day)) * 100 : 0,
      pnl: knownValue - cost, pnlPct: cost ? ((knownValue - cost) / cost) * 100 : 0, pnlKnown: known.length > 0,
      estimated: known.some((r) => r.estimated), incomplete: rows.filter((r) => r.incomplete).map((r) => r.symbol),
      unpriced: [] as string[], updatedAt,
    };
  }, [holdings, universe, updatedAt, totals]);
}

/** Calendar events with their local day in the user's zone (an evening UTC event can be tomorrow in Tokyo). */
export function useWeekEvents(): WeekEvent[] {
  const { events } = useCalendar();
  const tf = useTime();
  return useMemo(() => [...events].sort((a, b) => a.at.localeCompare(b.at))
    .map((e) => ({ ...e, day: tf.dateKey(e.at), d: Number(tf.dateKey(e.at).slice(8)), w: tf.dayShort(e.at) })), [events, tf]);
}

/** The next high-impact event of the week and the minutes left (calendar hero, overview tile). */
export function useMainEvent() {
  const all = useWeekEvents();
  const upcoming = all.filter((e) => Date.parse(e.at) > nowMs());
  const event = upcoming.find((e) => e.imp === 3) ?? upcoming.find((e) => e.imp === 2) ?? upcoming[0];
  const left = event ? Math.max(0, Math.round((Date.parse(event.at) - nowMs()) / 60000)) : 0;
  return { event, upcoming, hours: Math.floor(left / 60), minutes: left % 60 };
}

export function useCoin(symbol: string) {
  const { universe } = useMarket();
  return universe.find((c) => c.symbol === symbol)!;
}
